import React, { Children } from 'react';

import {
  arrayOf,
  bool,
  func,
  node,
  oneOf,
  oneOfType,
  shape,
  string
} from 'prop-types';

import ClassNames from 'classnames';

import BaseComponent from '../.internal/BaseComponent';

import LoadingSpinner from '../.deprecated/loading_spinner/LoadingSpinner';
import ButtonV2 from './ButtonV2';

import './Button.scss';

const CLASS_TYPES = {
  accent: 'accent-2',
  brand: 'positive',
  bare: 'bare',
  'bare-muted': 'bare-muted',
  link: 'link',
  negative: 'negative',
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  warm: 'accent-1'
};

const SIZES = ['sm', 'md', 'lg'];
const STANDARD_TYPES = ['primary', 'secondary', 'tertiary'];

export default class Button extends BaseComponent {
  constructor(props) {
    super(props);

    this.ref = React.createRef();
  }

  render() {
    const { type } = this.props;

    if (STANDARD_TYPES.includes(type)) {
      return <ButtonV2 {...this.props} buttonRef={this.ref} />;
    }

    const {
      children,
      className,
      disabled,
      id,
      isSubmit,
      loading,
      onBlur,
      onClick,
      onFocus,
      onMouseLeave,
      onMouseOut,
      onMouseOver,
      size,
      style
    } = this.props;

    const wrapTextNode = Children.map(children, child => {
      return typeof child === 'string' ? <span>{child}</span> : child;
    });

    const classNames = ClassNames(
      'button',
      className,
      CLASS_TYPES[type] && `button--${CLASS_TYPES[type]}`,
      SIZES.includes(size) && `button--${size}`
    );
    const loadingSpinnerClassNames = ClassNames(
      'button__loading-spinner',
      loading && 'visible'
    );

    return (
      <button
        className={classNames}
        disabled={disabled || loading}
        id={id}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        style={style}
        ref={this.ref}
        // Disabling rule here for ESLint doesn't allow
        // button `type` attribute (e.g., "submit", "button")
        // to be dynamic
        // eslint-disable-next-line react/button-has-type
        type={isSubmit ? 'submit' : 'button'}
      >
        <div className="button__content">
          {wrapTextNode}
          {loading && (
            <LoadingSpinner size="small" className={loadingSpinnerClassNames} />
          )}
        </div>
      </button>
    );
  }
}

Button.propTypes = {
  children: oneOfType([arrayOf(node), node]).isRequired,
  className: string,
  disabled: bool,
  id: string,
  isSubmit: bool,
  loading: bool,
  onBlur: func,
  onClick: func,
  onFocus: func,
  onMouseEnter: func,
  onMouseLeave: func,
  onMouseOut: func,
  onMouseOver: func,
  size: oneOf(['sm', 'md', 'lg']),
  style: shape({}),
  type: oneOf([
    'accent',
    'bare',
    'bare-muted',
    'brand',
    'default',
    'link',
    'negative',
    'primary',
    'secondary',
    'tertiary',
    'warm'
  ])
};

Button.defaultProps = {
  children: undefined,
  className: undefined,
  disabled: false,
  id: undefined,
  isSubmit: false,
  loading: false,
  onBlur: undefined,
  onClick: undefined,
  onFocus: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  onMouseOut: undefined,
  onMouseOver: undefined,
  size: undefined,
  style: undefined,
  type: 'primary'
};
